import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Loader2,
  Plus,
  Shield,
  Sliders,
  Sparkles,
  Tag,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../../context/AuthContext';
import epcProjectService, {
  type EpcProjectMembershipRow,
  type EpcProjectOrgUnit,
  type EpcProjectTeamRole,
} from '../../../../services/epcProjectService';
import { userService } from '../../../../services/userService';
import type { User as AppUser } from '../../../../types/user';
import { EpcTeamUserPicker } from './EpcTeamUserPicker';
import { EpcDeptMultiSelectDrawer } from './EpcDeptMultiSelectDrawer';
import {
  canDeactivateTarget,
  governanceMembers,
  isGovernanceRole,
  resolveTeamCapability,
  ROLE_LABEL,
} from './teamCapabilities';
import { filterDirectoryForEpcRole } from './epcRoleMatching';
import { useEpcRoles } from './useEpcRoles';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import { buildDepartmentHierarchies } from './hierarchyLayout';
import { EpcTeamFlowDiagram, type DiagramAddIntent } from './EpcTeamFlowDiagram';
import {
  ResourcePopupOverlay,
  type ResourcePopupData,
} from './ResourceInfoPopup';
import { EpcTeamHeader } from './EpcTeamHeader';
import { EpcTeamFlowHint } from './EpcTeamFlowHint';
import { addMemberBlockers } from './addMemberBlockers';
import TeamActionDialog, { type TeamDialogState } from './TeamActionDialog';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { SOVEREIGN } from '../../utils/epcProjectVisual';

/* Sovereign Emerald primary-button classes (flat racing green, engraved shadow) */
const PRIMARY_BTN =
  'border border-[rgba(11,59,46,0.45)] bg-[#264f3c] shadow-[0_8px_18px_-12px_rgba(38,79,60,0.5),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#2d5c47]';

/* ─── helpers ─── */
function orgId(u: EpcProjectOrgUnit): string {
  const r = u as { _id?: string; id?: string };
  return String(r.id ?? r._id ?? '');
}
function userId(authUser: unknown): string | undefined {
  if (!authUser || typeof authUser !== 'object') return undefined;
  const o = authUser as { _id?: string; id?: string };
  return String(o._id ?? o.id ?? '') || undefined;
}

/**
 * Static fallback list of the 7 canonical EPC tier tags. Used as a safety net
 * when the dynamic /roles endpoint is unreachable so the role dropdown never
 * goes empty. The live list comes from `useEpcRoles()` (which lists every
 * admin-created EPC role tagged with one of these 7 tier values — the tag is
 * optional on the Role, and untagged roles are filtered out).
 */
const FALLBACK_TIER_TAGS: EpcProjectTeamRole[] = [
  'epc_head', 'pmo', 'project_head', 'project_coordinator',
  'department_head', 'manager', 'site_engineer',
];

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */

export default function EpcTeamWorkspace({
  projectId,
  canUpdate,
  scheduleTasks = [],
  projectName,
}: {
  projectId: string;
  canUpdate: boolean;
  /** Current schedule tasks — used to surface cross-department date conflicts in the resource popup. */
  scheduleTasks?: EpcScheduleTaskRow[];
  /** Project display name for the workspace header. */
  projectName?: string;
}) {
  const { user: authUser, hasPermission } = useAuth();
  const myUid = userId(authUser);
  const isSuperAdmin = Boolean((authUser as { isSuperAdmin?: boolean })?.isSuperAdmin);
  const hasEpcUpdate = hasPermission('epcProject', 'update');

  /* ── state ── */
  const [orgUnits, setOrgUnits] = useState<EpcProjectOrgUnit[]>([]);
  const [memberships, setMemberships] = useState<EpcProjectMembershipRow[]>([]);
  const [directory, setDirectory] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* dept drawer */
  const [deptDrawerOpen, setDeptDrawerOpen] = useState(false);

  /* member drawer */
  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<EpcProjectTeamRole>('department_head');
  /**
   * Identifier of the admin-defined Role doc the user picked from the dropdown.
   * Empty when the drawer is opened via a diagram intent (we set `addRole` to
   * the tier tag, but no specific Role doc is associated yet — the form's
   * selector will default to the first Role with the matching tag).
   */
  const [addRoleId, setAddRoleId] = useState<string>('');
  const [addOrgIds, setAddOrgIds] = useState<string[]>([]);
  const [addReportsTo, setAddReportsTo] = useState('');
  /** Tracks whether the drawer was opened from a specific diagram action (so role / dept are auto-resolved). */
  const [drawerAutoResolved, setDrawerAutoResolved] = useState(false);
  /** Sovereign popup for conflict errors and add-anyway confirmations. */
  const [teamDialog, setTeamDialog] = useState<TeamDialogState | null>(null);
  const [drawerAdjustOpen, setDrawerAdjustOpen] = useState(false);

  /* manager shortcut */
  const [engUserId, setEngUserId] = useState('');

  /* focus editor */
  const [focusDeptId, setFocusDeptId] = useState<string | null>(null);
  const [focusDraft, setFocusDraft] = useState('');

  /* resource popup */
  const [popupData, setPopupData] = useState<ResourcePopupData | null>(null);

  /* ── load ── */
  /* Core team data — org units + memberships. Fast; drives the diagram and the
     `loading` gate, and is the ONLY thing re-fetched after a mutation. */
  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const [ou, mem] = await Promise.all([
        epcProjectService.listOrgUnits(projectId),
        epcProjectService.listMemberships(projectId),
      ]);
      setOrgUnits(ou);
      setMemberships(mem);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  /* User directory — only used by the Add-Member picker and as a display-name
     fallback. Loaded once in parallel so it never blocks the diagram, and NOT
     re-fetched after mutations (assigning members doesn't change the global
     user list). This keeps the heavy 500-user query off the critical path. */
  const loadDirectory = useCallback(async () => {
    if (!projectId) return;
    try {
      // Backend populates role and roles.roleId by default — needed for
      // EPC role-based filtering of the team picker.
      const dir = await userService.getUsers({ limit: 500, status: 'active' });
      setDirectory(dir.users ?? []);
    } catch {
      setDirectory([]);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
    void loadDirectory();
  }, [load, loadDirectory]);

  /* ── EPC roles (dynamic) ──
   * Live list of admin-defined EPC roles, each carrying a tier `tag`. The
   * Add-Member dropdown shows `Role.name` (human-friendly, admin-controlled)
   * and on submit we forward `Role.tag` as `membership.role` — keeping the
   * strict 7-value enum on EpcProjectMembership.role unchanged. */
  const epcRolesQ = useEpcRoles();
  const epcRoles = epcRolesQ.data ?? [];

  /* ── derived ── */
  const { cap, mine } = useMemo(
    () => resolveTeamCapability(isSuperAdmin, hasEpcUpdate, myUid, memberships),
    [isSuperAdmin, hasEpcUpdate, myUid, memberships]
  );
  /**
   * No restrictions on department-level assignments: a member can be added to
   * any number of departments and the same person can appear multiple times
   * across departments without limit. Governance seats (PMO / Project Head /
   * etc.) remain one-per-person because those roles are mutually exclusive at
   * the project level.
   */
  const excludeForSlot = useCallback(
    (role: EpcProjectTeamRole, _slotOrgId?: string): Set<string> => {
      if (!isGovernanceRole(role)) return new Set<string>();
      const set = new Set<string>();
      for (const m of memberships) {
        if (!m.inactive && isGovernanceRole(m.role)) set.add(m.userId);
      }
      return set;
    },
    [memberships],
  );
  /** Project-wide assigned ids (kept for backwards-compat use sites that don't care about slot). */
  const assignedIds = useMemo(() => new Set(memberships.map((m) => m.userId)), [memberships]);
  const govList = useMemo(() => governanceMembers(memberships), [memberships]);

  /** What's still missing before the Add-Member drawer can submit (drives the footer button). */
  const drawerBlockers = useMemo(
    () => addMemberBlockers({ role: addRole, userId: addUserId, orgIds: addOrgIds, reportsTo: addReportsTo }),
    [addRole, addUserId, addOrgIds, addReportsTo],
  );

  /**
   * Build a directory + role-label map filtered to users whose User-Management
   * role matches the requested EPC team role (and lives in the EPC department).
   */
  const buildEpcCandidates = useCallback(
    (epcRole: EpcProjectTeamRole) => {
      const matched = filterDirectoryForEpcRole(directory, epcRole);
      const users = matched.map((m) => m.user);
      const roleLabelByUserId = new Map<string, string | undefined>();
      for (const m of matched) {
        const id = String((m.user as { _id?: string; id?: string })._id ?? (m.user as { id?: string }).id ?? '');
        if (id) roleLabelByUserId.set(id, m.epcRoleLabel);
      }
      return { users, roleLabelByUserId };
    },
    [directory],
  );

  const managerCandidates = useMemo(() => buildEpcCandidates('manager'), [buildEpcCandidates]);
  const engineerCandidates = useMemo(() => buildEpcCandidates('site_engineer'), [buildEpcCandidates]);
  const headCandidates = useMemo(() => buildEpcCandidates('department_head'), [buildEpcCandidates]);
  const drawerCandidates = useMemo(() => buildEpcCandidates(addRole), [buildEpcCandidates, addRole]);
  const departmentTrees = useMemo(() => buildDepartmentHierarchies(orgUnits, memberships), [orgUnits, memberships]);

  const headsByOrg = useMemo(() => {
    const map = new Map<string, EpcProjectMembershipRow>();
    for (const m of memberships) {
      // Ignore soft-deleted heads so a deactivated head doesn't mask a
      // department that actually needs one (drives the "needs head" stat).
      if (m.role === 'department_head' && m.orgUnitId && !m.inactive) map.set(m.orgUnitId, m);
    }
    return map;
  }, [memberships]);

  const managersByOrg = useMemo(() => {
    const map = new Map<string, EpcProjectMembershipRow[]>();
    for (const m of memberships) {
      if (m.role === 'manager' && m.orgUnitId) {
        const arr = map.get(m.orgUnitId) ?? [];
        arr.push(m);
        map.set(m.orgUnitId, arr);
      }
    }
    return map;
  }, [memberships]);

  /* userId → number of departments this person is in (for multi-dept badge) */
  const memberDeptCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of memberships) {
      if (m.orgUnitId) map.set(m.userId, (map.get(m.userId) ?? 0) + 1);
    }
    return map;
  }, [memberships]);

  /* ── header stats ── */
  const activeOrgUnits = useMemo(
    () => orgUnits.filter((o) => !(o as { inactive?: boolean }).inactive),
    [orgUnits],
  );
  const deptCount = activeOrgUnits.length;
  const memberCount = useMemo(
    () => new Set(memberships.filter((m) => !m.inactive).map((m) => m.userId)).size,
    [memberships],
  );
  const deptsWithoutHead = useMemo(
    () => activeOrgUnits.filter((o) => !headsByOrg.has(orgId(o))).length,
    [activeOrgUnits, headsByOrg],
  );
  const attentionCount = deptsWithoutHead;
  const headerEyebrow = useMemo(() => {
    const word =
      cap === 'governance_full'
        ? mine
          ? ROLE_LABEL[mine.role]
          : 'Governance'
        : cap === 'dept_head'
          ? 'Department head'
          : cap === 'manager'
            ? 'Manager'
            : cap === 'bootstrap'
              ? 'Setup'
              : 'Viewer';
    return `${word} · Team`;
  }, [cap, mine]);

  /** userId → display name (memberships first — already curated; fall back to directory). */
  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of memberships) {
      if (m.userName) map.set(m.userId, m.userName);
    }
    for (const u of directory) {
      const uid = String((u as { _id?: string; id?: string })._id ?? (u as { id?: string }).id ?? '');
      if (uid && !map.has(uid) && u.name) map.set(uid, u.name);
    }
    return map;
  }, [memberships, directory]);

  const orgNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const ou of orgUnits) map.set(orgId(ou), ou.name);
    return map;
  }, [orgUnits]);

  /* If the role changes, clear any user that was picked under the previous filter
     so we don't show a "selected" user who no longer matches. Site engineers hold
     one membership per project, so trim any multi-dept selection down to one. */
  useEffect(() => {
    setAddUserId('');
    if (addRole === 'site_engineer') {
      setAddOrgIds((prev) => (prev.length > 1 ? [prev[0]!] : prev));
    }
  }, [addRole]);

  /* Keep `addRoleId` in sync with `addRole`. If the role tag is set (by an
   * intent or drawer reset) but the currently-selected Role doc doesn't match
   * that tag, clear the doc selection so the dropdown falls back to the first
   * matching role (or the fallback tag option). */
  useEffect(() => {
    if (!addRoleId) return;
    const picked = epcRoles.find((r) => r.id === addRoleId);
    if (!picked || picked.tag !== addRole) setAddRoleId('');
  }, [addRole, addRoleId, epcRoles]);

  /* auto-fill reportsTo — only applies when exactly 1 dept is selected (or role is dept_head) */
  useEffect(() => {
    const firstOrgId = addOrgIds[0];
    if (addRole === 'manager' && addOrgIds.length === 1 && firstOrgId) {
      const head = headsByOrg.get(firstOrgId);
      if (head) setAddReportsTo(head.userId);
      return;
    }
    if (addRole === 'manager' && addOrgIds.length !== 1) {
      setAddReportsTo('');
      return;
    }
    if (addRole === 'site_engineer' && addOrgIds.length === 1 && firstOrgId) {
      const mgrs = (managersByOrg.get(firstOrgId) ?? []).filter((m) => !m.inactive);
      setAddReportsTo((prev) => {
        // Keep an explicitly-set manager (e.g. from a diagram "+ Add engineer"
        // intent) as long as they really manage this department — only
        // auto-fill when nothing valid is selected yet.
        if (prev && mgrs.some((m) => m.userId === prev)) return prev;
        return mgrs.length === 1 ? mgrs[0]!.userId : '';
      });
      return;
    }
    if (addRole === 'site_engineer' && addOrgIds.length !== 1) {
      setAddReportsTo('');
      return;
    }
    if (addRole === 'department_head' && govList.length === 1) {
      setAddReportsTo(govList[0]!.userId);
      return;
    }
    if (addRole === 'department_head' && govList.length === 0) setAddReportsTo('');
  }, [addRole, addOrgIds, govList, headsByOrg, managersByOrg]);

  /* capability flags */
  const showGovernanceForm = cap === 'governance_full';
  const showDeptHeadManagerForm = cap === 'dept_head' && mine?.orgUnitId;
  const showManagerEngineerForm = cap === 'manager';

  useEffect(() => { if (!showGovernanceForm) setMemberDrawerOpen(false); }, [showGovernanceForm]);
  useEffect(() => { if (!canUpdate || !showGovernanceForm) setDeptDrawerOpen(false); }, [canUpdate, showGovernanceForm]);

  /* ── resource popup ── */
  const closePopup = useCallback(() => setPopupData(null), []);

  const openPopup = useCallback(
    (m: EpcProjectMembershipRow) => {
      const allMemberships = memberships.filter((x) => x.userId === m.userId);
      const alreadyInOrgIds = new Set(allMemberships.map((x) => x.orgUnitId).filter(Boolean));
      const availableDepts = orgUnits.filter((o) => !alreadyInOrgIds.has(orgId(o)));
      const canMove = showGovernanceForm || (cap === 'manager' && myUid !== undefined);
      const canCopy = showGovernanceForm;
      const memberTasks = scheduleTasks.filter((t) => t.assignedUserId === m.userId);
      setPopupData({
        member: m,
        allMemberships,
        orgUnits,
        availableDepts,
        canMove,
        canCopy,
        memberTasks,
      });
    },
    [memberships, orgUnits, showGovernanceForm, cap, myUid, scheduleTasks]
  );

  /**
   * Resolve who a `role` reports to when copied/moved into `targetOrgId`.
   *  • department_head → first governance member (PMO / Project Head)
   *  • manager         → dept head of the target dept
   *  • site_engineer   → an existing manager in the target dept
   */
  const resolveReportsToFor = useCallback(
    (
      role: EpcProjectTeamRole,
      targetOrgId: string,
      override?: string,
    ): { userId?: string; reason?: string } => {
      if (override) return { userId: override };
      if (role === 'department_head') {
        const g = govList[0]?.userId;
        return g
          ? { userId: g }
          : { reason: 'No governance member found — add a PMO or Project Head first.' };
      }
      if (role === 'manager') {
        const head = headsByOrg.get(targetOrgId)?.userId;
        return head
          ? { userId: head }
          : { reason: 'Target department has no head yet — add a department head first.' };
      }
      if (role === 'site_engineer') {
        const mgr = managersByOrg.get(targetOrgId)?.[0]?.userId;
        return mgr
          ? { userId: mgr }
          : { reason: 'No manager found in target department — add a manager first.' };
      }
      return {};
    },
    [govList, headsByOrg, managersByOrg],
  );

  const onCopyToDept = useCallback(
    async (targetOrgId: string, targetReportsToId?: string) => {
      if (!popupData || !projectId) return;
      const member = popupData.member;
      const { userId: reportsTo, reason } = resolveReportsToFor(
        member.role,
        targetOrgId,
        targetReportsToId,
      );
      if (!reportsTo) {
        toast.error(reason ?? 'Could not resolve reporting line for the new department.');
        return;
      }
      try {
        setSaving(true);
        await epcProjectService.createMembership(projectId, {
          userId: member.userId,
          role: member.role,
          orgUnitId: targetOrgId,
          reportsToUserId: reportsTo,
        });
        toast.success(`${member.userName ?? 'Member'} copied to new department`);
        closePopup();
        await load();
      } catch (err) {
        console.error(err);
        const msg = err instanceof Error && err.message ? err.message : 'Could not copy member';
        toast.error(msg);
      } finally {
        setSaving(false);
      }
    },
    [popupData, projectId, resolveReportsToFor, closePopup, load]
  );

  const onMoveToDept = useCallback(
    async (targetOrgId: string, targetReportsToId?: string) => {
      if (!popupData || !projectId) return;
      const member = popupData.member;
      const { userId: reportsTo, reason } = resolveReportsToFor(
        member.role,
        targetOrgId,
        targetReportsToId,
      );
      if (!reportsTo) {
        toast.error(reason ?? 'Could not resolve reporting line for the new department.');
        return;
      }
      if (!window.confirm(`Move ${member.userName ?? 'this member'} to the selected department? They will be removed from the current one.`)) return;
      try {
        setSaving(true);
        await epcProjectService.updateMembership(projectId, member.id, { inactive: true });
        await epcProjectService.createMembership(projectId, {
          userId: member.userId,
          role: member.role,
          orgUnitId: targetOrgId,
          reportsToUserId: reportsTo,
        });
        toast.success(`${member.userName ?? 'Member'} moved to new department`);
        closePopup();
        await load();
      } catch (err) {
        console.error(err);
        const msg = err instanceof Error && err.message ? err.message : 'Could not move member';
        toast.error(msg);
      } finally {
        setSaving(false);
      }
    },
    [popupData, projectId, resolveReportsToFor, closePopup, load]
  );

  /* ── diagram permission callbacks ── */
  const canAddHeadInDiagram = useCallback((_oid: string) => Boolean(showGovernanceForm), [showGovernanceForm]);
  const canAddManagerForOrgDiagram = useCallback(
    (oid: string) => {
      if (!headsByOrg.get(oid)) return false;
      if (showGovernanceForm) return true;
      if (showDeptHeadManagerForm && mine?.orgUnitId === oid) return true;
      return false;
    },
    [headsByOrg, mine?.orgUnitId, showDeptHeadManagerForm, showGovernanceForm]
  );
  const canAddEngineerForManagerDiagram = useCallback(
    (_oid: string, managerUserId: string) => {
      if (showGovernanceForm) return true;
      if (showManagerEngineerForm && myUid === managerUserId) return true;
      return false;
    },
    [myUid, showGovernanceForm, showManagerEngineerForm]
  );

  /* ── intent handler from diagram ── */
  const handleIntent = useCallback(
    (intent: DiagramAddIntent) => {
      const open = (autoResolved: boolean) => {
        setDrawerAutoResolved(autoResolved);
        setDrawerAdjustOpen(false);
        setMemberDrawerOpen(true);
      };
      if (intent.kind === 'governance') {
        setAddRole('pmo'); setAddOrgIds([]); setAddReportsTo(''); setAddUserId('');
        open(false); return;
      }
      if (intent.kind === 'department_head') {
        setAddRole('department_head'); setAddOrgIds([intent.orgId]);
        setAddReportsTo(govList[0]?.userId ?? ''); setAddUserId('');
        open(true); return;
      }
      if (intent.kind === 'manager') {
        if (cap === 'dept_head') { setAddUserId(''); return; }
        setAddRole('manager'); setAddOrgIds([intent.orgId]);
        setAddReportsTo(intent.reportsToUserId); setAddUserId('');
        open(true); return;
      }
      if (intent.kind === 'site_engineer') {
        if (cap === 'manager') { setEngUserId(''); return; }
        setAddRole('site_engineer'); setAddOrgIds([intent.orgId]);
        setAddReportsTo(intent.managerUserId); setAddUserId('');
        open(true); return;
      }
    },
    [cap, govList]
  );

  /* ── actions ── */
  const onBootstrap = async (role: 'pmo' | 'project_head') => {
    if (!projectId || !myUid) return;
    try {
      setSaving(true);
      await epcProjectService.createMembership(projectId, { userId: myUid, role });
      toast.success('You are now the first governance member');
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Bootstrap failed');
    } finally {
      setSaving(false);
    }
  };

  const onAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !addUserId) return;

    if (isGovernanceRole(addRole)) {
      const body: { userId: string; role: EpcProjectTeamRole; reportsToUserId?: string } = { userId: addUserId, role: addRole };
      if (addReportsTo) body.reportsToUserId = addReportsTo;
      try {
        setSaving(true);
        await epcProjectService.createMembership(projectId, body);
        toast.success('Member added');
        setAddUserId(''); setAddRoleId(''); setDrawerAutoResolved(false); setDrawerAdjustOpen(false); setMemberDrawerOpen(false);
        await load();
      } catch (err) {
        console.error(err);
        toast.error(err instanceof Error && err.message ? err.message : 'Could not add member');
      } finally { setSaving(false); }
      return;
    }

    if (addRole === 'department_head' || addRole === 'manager' || addRole === 'site_engineer') {
      if (addOrgIds.length === 0) { toast.error('Select at least one department'); return; }
      if (addRole === 'department_head' && !addReportsTo) { toast.error('Select who this person reports to'); return; }

      const memberName = userNameById.get(addUserId) ?? 'This member';

      const performDeptAdd = async () => {
        setSaving(true);
        let successCount = 0;
        for (const deptId of addOrgIds) {
          const deptLabel = orgNameById.get(deptId) ?? deptId;
          let reportsToUserId: string | undefined;
          if (addRole === 'department_head') {
            reportsToUserId = addReportsTo;
          } else if (addRole === 'manager') {
            const head = headsByOrg.get(deptId);
            if (!head) { toast.error(`No dept head in "${deptLabel}" — add a head first`); continue; }
            reportsToUserId = head.userId;
          } else {
            if (!addReportsTo) { toast.error('Select which manager this engineer reports to'); continue; }
            reportsToUserId = addReportsTo;
          }
          try {
            await epcProjectService.createMembership(projectId, { userId: addUserId, role: addRole, orgUnitId: deptId, reportsToUserId });
            successCount++;
          } catch (err) {
            console.error(err);
            const msg = err instanceof Error && err.message ? err.message : 'Could not add member';
            const httpCode = (err as { code?: number }).code;
            if (httpCode === 409 || /already/i.test(msg)) {
              setTeamDialog({
                kind: 'error',
                title: 'Already on this team',
                message: msg,
                context: [
                  { label: 'Member', value: memberName },
                  { label: 'Department', value: deptLabel },
                  ...(reportsToUserId
                    ? [{ label: 'Reports to', value: userNameById.get(reportsToUserId) ?? '—' }]
                    : []),
                ],
              });
            } else {
              toast.error(`${deptLabel}: ${msg}`);
            }
          }
        }
        setSaving(false);
        if (successCount > 0) {
          toast.success(successCount === 1 ? 'Member added' : `Member added to ${successCount} departments`);
          setAddUserId(''); setAddRoleId(''); setAddOrgIds([]); setAddReportsTo('');
          setDrawerAutoResolved(false); setDrawerAdjustOpen(false); setMemberDrawerOpen(false);
          await load();
        }
      };

      if (addRole === 'site_engineer') {
        const deptName = orgNameById.get(addOrgIds[0] ?? '') ?? 'this department';
        const managerName = userNameById.get(addReportsTo) ?? 'this manager';
        const existing = memberships.filter(
          (m) => m.userId === addUserId && m.role === 'site_engineer' && !m.inactive
        );
        const exactDup = existing.find(
          (m) => m.orgUnitId === addOrgIds[0] && m.reportsToUserId === addReportsTo
        );
        if (exactDup) {
          setTeamDialog({
            kind: 'error',
            title: 'Already on this team',
            message: `${memberName} already reports to ${managerName} in ${deptName} on this project — the same assignment can't be added twice.`,
            context: [
              { label: 'Engineer', value: memberName },
              { label: 'Department', value: deptName },
              { label: 'Reports to', value: managerName },
            ],
          });
          return;
        }
        if (existing.length > 0) {
          setTeamDialog({
            kind: 'confirm',
            title: 'Add another assignment?',
            message: `${memberName} is already a site engineer on this project. Add another assignment under ${managerName} in ${deptName} as well?`,
            assignments: existing.map((m) => {
              const dept = m.orgUnitName ?? orgNameById.get(m.orgUnitId ?? '') ?? 'a department';
              const mgr = m.reportsToUserId ? (userNameById.get(m.reportsToUserId) ?? 'another manager') : 'another manager';
              return `${dept} — under ${mgr}`;
            }),
            confirmLabel: 'Add anyway',
            onConfirm: () => {
              setTeamDialog(null);
              void performDeptAdd();
            },
          });
          return;
        }
      }

      await performDeptAdd();
    }
  };

  const onAddEngineerShortcut = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !engUserId) return;
    try {
      setSaving(true);
      await epcProjectService.managerAddSiteEngineer(projectId, engUserId);
      toast.success('Site engineer added under you');
      setEngUserId(''); await load();
    } catch (err) {
      console.error(err); toast.error('Only managers can use this shortcut');
    } finally {
      setSaving(false);
    }
  };

  const onDeactivate = async (m: EpcProjectMembershipRow) => {
    if (!projectId) return;
    /* Confirmation is collected by the diagram's right-side drawer. */
    try {
      setSaving(true);
      await epcProjectService.updateMembership(projectId, m.id, { inactive: true });
      toast.success('Membership ended');
      await load();
    } catch (err) {
      console.error(err); toast.error('Could not remove');
    } finally {
      setSaving(false);
    }
  };

  const onRemoveDept = async (ou: EpcProjectOrgUnit) => {
    const id = orgId(ou);
    if (!projectId || !id) return;
    /* Confirmation is collected by the diagram's right-side drawer. */
    try {
      setSaving(true);
      await epcProjectService.updateOrgUnit(projectId, id, { inactive: true });
      toast.success(`Department "${ou.name}" removed`);
      await load();
    } catch (err) {
      console.error(err); toast.error('Could not remove department');
    } finally {
      setSaving(false);
    }
  };

  const onSaveFocus = async (ou: EpcProjectOrgUnit) => {
    const id = orgId(ou);
    if (!projectId || !id) return;
    const focusAreas = focusDraft.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean).slice(0, 24);
    try {
      setSaving(true);
      await epcProjectService.updateOrgUnit(projectId, id, { focusAreas });
      toast.success('Focus areas saved');
      await load(); setFocusDeptId(null);
    } catch (err) {
      console.error(err); toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const openFocusEditor = (ou: EpcProjectOrgUnit) => {
    setFocusDeptId(orgId(ou));
    setFocusDraft((ou.focusAreas ?? []).join(', '));
  };

  /* Lifted add-handlers — shared by the header and the diagram's inline affordances. */
  const openAddMember = useCallback(() => {
    setAddRole('pmo');
    setAddOrgIds([]);
    setAddReportsTo('');
    setAddUserId('');
    setDrawerAutoResolved(false);
    setDrawerAdjustOpen(true);
    setMemberDrawerOpen(true);
  }, []);
  const openAddDepartment = useCallback(() => setDeptDrawerOpen(true), []);

  /* ── loading ── */
  if (loading && !orgUnits.length && !memberships.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-3xl text-white"
          style={{
            background: SOVEREIGN.btn,
            border: `1px solid ${SOVEREIGN.btnBorder}`,
            boxShadow: SOVEREIGN.btnShadow,
          }}
        >
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
        <p className="text-sm font-semibold text-[#7C8A82]">Loading team architecture…</p>
      </div>
    );
  }

  /* ══════════════════════════════════ RENDER ══════════════════════════════════ */
  return (
    <div className="space-y-5">

      {/* ── Command-center header ── */}
      <EpcTeamHeader
        eyebrow={headerEyebrow}
        projectName={projectName}
        subtitle="Org structure & reporting lines"
        deptCount={deptCount}
        memberCount={memberCount}
        attentionCount={attentionCount}
        onAddDepartment={canUpdate && showGovernanceForm ? openAddDepartment : undefined}
      />

      {/* ── Slim flow hint ── */}
      <EpcTeamFlowHint />

      {/* ── Bootstrap banner ── */}
      {cap === 'bootstrap' && canUpdate ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden rounded-2xl border-2 border-dashed p-5"
          style={{
            borderColor: 'rgba(201,169,97,0.55)',
            background: SOVEREIGN.cardBg,
            boxShadow: SOVEREIGN.cardShadow,
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white"
              style={{
                background: SOVEREIGN.btn,
                border: `1px solid ${SOVEREIGN.btnBorder}`,
                boxShadow: SOVEREIGN.btnShadow,
              }}
            >
              <Shield className="h-5 w-5" />
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-extrabold text-[#0B3B2E]">Start this project's team</p>
                <p className="mt-0.5 text-sm text-[#7C8A82]">No members yet. Add yourself as the first governance seat.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving || !myUid}
                  onClick={() => void onBootstrap('pmo')}
                  className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-black text-white disabled:opacity-50 ${PRIMARY_BTN}`}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  I am PMO
                </button>
                <button
                  type="button"
                  disabled={saving || !myUid}
                  onClick={() => void onBootstrap('project_head')}
                  className="inline-flex items-center gap-2 rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-5 py-2.5 text-sm font-black text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[#F8F2E2] disabled:opacity-50"
                >
                  I am Project Head
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}

      {/* ══ ORG CHART DIAGRAM ══ */}
      <EpcTeamFlowDiagram
        governance={govList}
        departments={departmentTrees}
        cap={cap}
        showGovernanceChrome={showGovernanceForm}
        canGovernanceAdd={showGovernanceForm}
        canAddHead={canAddHeadInDiagram}
        canAddManagerForOrg={canAddManagerForOrgDiagram}
        canAddEngineerForManager={canAddEngineerForManagerDiagram}
        onIntent={handleIntent}
        onRemoveMember={(m) => void onDeactivate(m)}
        canRemoveMember={(m) => canDeactivateTarget(cap, isSuperAdmin, myUid, mine, m)}
        saving={saving}
        showEditFocus={showGovernanceForm}
        onEditFocus={openFocusEditor}
        onOpenAddMember={showGovernanceForm ? openAddMember : undefined}
        onOpenAddDepartment={canUpdate && showGovernanceForm ? openAddDepartment : undefined}
        showAddMemberButton={showGovernanceForm}
        showAddDepartmentButton={Boolean(canUpdate && showGovernanceForm)}
        onCardClick={openPopup}
        memberDeptCountMap={memberDeptCountMap}
        onRemoveDepartment={canUpdate ? (o) => void onRemoveDept(o) : undefined}
      />

      {/* ══ Resource Info Popup ══ */}
      <ResourcePopupOverlay
        data={popupData}
        actions={{
          onCopyToDept: (orgId, reportsTo) => void onCopyToDept(orgId, reportsTo),
          onMoveToDept: (orgId, reportsTo) => void onMoveToDept(orgId, reportsTo),
          onClose: closePopup,
        }}
      />

      {/* ── Focus area editor modal ── */}
      <AnimatePresence>
        {focusDeptId && showGovernanceForm ? (
          (() => {
            const ou = orgUnits.find((o) => orgId(o) === focusDeptId);
            if (!ou) return null;
            return (
              <motion.div
                key="focus-modal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                  onClick={() => setFocusDeptId(null)}
                />
                <motion.div
                  initial={{ scale: 0.94, y: 16 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.94, y: 16 }}
                  className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl"
                  style={{
                    background: 'rgba(255,254,251,0.98)',
                    border: `1px solid ${SOVEREIGN.cardBorder}`,
                    boxShadow: SOVEREIGN.cardShadow,
                  }}
                >
                  <div
                    className="flex items-center justify-between gap-2 px-5 py-4"
                    style={{ background: '#F8F2E2', borderBottom: '1px solid rgba(11,59,46,0.18)' }}
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-[#1F5A47]" />
                      <p className="text-sm font-extrabold text-[#0B3B2E]">Focus Tags — {ou.name}</p>
                    </div>
                    <button
                      type="button"
                      className="rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] p-1.5 text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)]"
                      onClick={() => setFocusDeptId(null)}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="p-5 space-y-3">
                    <textarea
                      className="w-full rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-2.5 text-sm text-[#0B3B2E] outline-none transition focus:border-[#0B3B2E] focus:ring-[3px] focus:ring-[rgba(166,206,57,0.22)]"
                      rows={4}
                      placeholder="Comma-separated: WBS L4, Electrical BOQ, Concrete…"
                      value={focusDraft}
                      onChange={(e) => setFocusDraft(e.target.value)}
                      autoFocus
                    />
                    <p className="text-[11px] text-[#7C8A82]">
                      These keywords are matched against schedule task names for auto-routing.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void onSaveFocus(ou)}
                        className={`flex-1 rounded-xl py-2.5 text-sm font-black text-white disabled:opacity-50 ${PRIMARY_BTN}`}
                      >
                        {saving ? 'Saving…' : 'Save tags'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFocusDeptId(null)}
                        className="flex-1 rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2.5 text-sm font-semibold text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[#F8F2E2]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            );
          })()
        ) : null}
      </AnimatePresence>

      {/* ── Dept-head add-manager shortcut (inline card) ── */}
      {showDeptHeadManagerForm ? (
        <motion.form
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          id="epc-team-form-dept-head"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!projectId || !mine?.orgUnitId || !myUid || !addUserId) return;
            try {
              setSaving(true);
              await epcProjectService.createMembership(projectId, {
                userId: addUserId, role: 'manager',
                orgUnitId: mine.orgUnitId, reportsToUserId: myUid,
              });
              toast.success('Manager added');
              setAddUserId(''); await load();
            } catch (err) {
              console.error(err);
              const msg = err instanceof Error && err.message ? err.message : 'Could not add manager';
              toast.error(msg);
            } finally {
              setSaving(false);
            }
          }}
          className="overflow-hidden rounded-2xl border p-5 scroll-mt-28"
          style={{
            borderColor: 'rgba(201,169,97,0.55)',
            background: 'rgba(201,169,97,0.12)',
            boxShadow: SOVEREIGN.cardShadow,
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: 'rgba(201,169,97,0.14)',
                border: '1px solid rgba(11,59,46,0.20)',
                color: '#0B3B2E',
              }}
            >
              <UserPlus className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-extrabold text-[#8A5A08]">Add manager in your department</p>
              <p className="text-xs text-[#7C8A82]">They will report to you as department head.</p>
            </div>
          </div>
          <EpcTeamUserPicker
            users={managerCandidates.users} value={addUserId} onChange={setAddUserId}
            disabled={saving} excludeUserIds={excludeForSlot('manager', mine?.orgUnitId)}
            roleLabelByUserId={managerCandidates.roleLabelByUserId}
            placeholder="Search EPC managers by name or email…"
            emptyHint="No EPC managers found. Assign a Manager role in EPC from User Management."
          />
          <button
            type="submit" disabled={saving || !addUserId}
            className={`mt-3 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black text-white disabled:opacity-50 ${PRIMARY_BTN}`}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add manager
          </button>
        </motion.form>
      ) : null}

      {/* ── Manager add-engineer shortcut (inline card) ── */}
      {showManagerEngineerForm ? (
        <motion.form
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          id="epc-team-form-manager"
          onSubmit={onAddEngineerShortcut}
          className="overflow-hidden rounded-2xl border p-5 scroll-mt-28"
          style={{
            borderColor: 'rgba(201,169,97,0.55)',
            background: 'rgba(201,169,97,0.12)',
            boxShadow: SOVEREIGN.cardShadow,
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
              style={{
                background: SOVEREIGN.btn,
                border: `1px solid ${SOVEREIGN.btnBorder}`,
                boxShadow: SOVEREIGN.btnShadow,
              }}
            >
              {/* hard-hat SVG */}
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path d="M2 11 Q2 5 9 4 Q16 5 16 11 Z" fill="white" opacity="0.9" />
                <rect x={1.5} y={10.5} width={15} height={3.5} rx={1.5} fill="white" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-extrabold text-[#1F5A47]">Add site engineer (reports to you)</p>
              <p className="text-xs text-[#7C8A82]">Engineer will be linked under your management chain.</p>
            </div>
          </div>
          <EpcTeamUserPicker
            users={engineerCandidates.users} value={engUserId} onChange={setEngUserId}
            disabled={saving} excludeUserIds={excludeForSlot('site_engineer', mine?.orgUnitId)}
            roleLabelByUserId={engineerCandidates.roleLabelByUserId}
            placeholder="Search EPC site engineers by name or email…"
            emptyHint="No EPC site engineers found. Assign a Site Engineer role in EPC from User Management."
          />
          <button
            type="submit" disabled={saving || !engUserId}
            className={`mt-3 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black text-white disabled:opacity-50 ${PRIMARY_BTN}`}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Link site engineer
          </button>
        </motion.form>
      ) : null}

      {/* ════════════════════ ADD DEPARTMENTS DRAWER (multi-select, spec §3.2) ════════════════════ */}
      <EpcDeptMultiSelectDrawer
        open={deptDrawerOpen}
        onClose={() => setDeptDrawerOpen(false)}
        projectId={projectId}
        orgUnits={orgUnits}
        memberships={memberships}
        headCandidates={headCandidates}
        onMutated={load}
      />

      {/* ════════════════════ ADD MEMBER DRAWER ════════════════════ */}
      <RightDrawer
        open={memberDrawerOpen}
        onClose={() => {
          setMemberDrawerOpen(false);
          setDrawerAutoResolved(false);
          setDrawerAdjustOpen(false);
          setAddRoleId('');
          setAddOrgIds([]);
        }}
        title="Add team member"
        size="60"
        footer={
          <div className="flex items-center justify-between gap-3 border-t border-[rgba(11,59,46,0.12)] bg-[rgba(250,247,238,0.96)] px-6 py-4">
            <p className="min-w-0 truncate text-[11px] font-semibold" style={{ color: drawerBlockers.length ? '#8A5A08' : '#339274' }} title={drawerBlockers.join(' · ')}>
              {drawerBlockers.length ? `Missing: ${drawerBlockers.join(' · ')}` : 'Ready to add'}
            </p>
            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                onClick={() => {
                  setMemberDrawerOpen(false);
                  setDrawerAutoResolved(false);
                  setDrawerAdjustOpen(false);
                  setAddRoleId('');
                  setAddOrgIds([]);
                }}
                className="rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-4 py-2.5 text-sm font-semibold text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[#F8F2E2]"
              >
                Cancel
              </button>
              <button
                type="submit" form="epc-form-add-member"
                disabled={saving || drawerBlockers.length > 0}
                title={drawerBlockers.length ? drawerBlockers.join(' · ') : undefined}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-black text-white disabled:opacity-50 ${PRIMARY_BTN}`}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Add to team
              </button>
            </div>
          </div>
        }
      >
        {(() => {
          const needsDept =
            addRole === 'department_head' || addRole === 'manager' || addRole === 'site_engineer';
          const reportsToName = addReportsTo ? userNameById.get(addReportsTo) : undefined;
          const reportsToRole = (() => {
            if (!addReportsTo) return undefined;
            const m = memberships.find((x) => x.userId === addReportsTo);
            return m ? ROLE_LABEL[m.role] : undefined;
          })();
          const hintForRole =
            addRole === 'department_head'
              ? 'Reports to a governance member (PMO / Project Head).'
              : addRole === 'manager'
                ? "Auto-reports to each department's head."
                : null;
          /* Site engineers must name their manager — if it isn't resolved yet,
             keep the details section open so the manager select is visible. */
          const showAdjust =
            drawerAdjustOpen || !drawerAutoResolved || (addRole === 'site_engineer' && !addReportsTo);
          return (
            <form id="epc-form-add-member" onSubmit={onAddMember} className="space-y-5">
              {/* Auto-resolved context summary */}
              <div
                className="overflow-hidden rounded-2xl border p-4"
                style={{
                  borderColor: SOVEREIGN.cardBorder,
                  background: '#F8F2E2',
                  boxShadow: SOVEREIGN.cardShadow,
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{
                      background: 'rgba(201,169,97,0.14)',
                      border: '1px solid rgba(11,59,46,0.20)',
                      color: '#0B3B2E',
                    }}
                  >
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#1F5A47]">
                      {drawerAutoResolved ? 'Auto-detected context' : 'New team member'}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-sm">
                      <span className="text-[#2A1F17]">Adding</span>
                      <span className="inline-flex items-center gap-1 rounded-lg bg-[rgba(255,254,251,0.96)] px-2 py-1 text-xs font-bold text-[#0B3B2E] shadow-sm ring-1 ring-[rgba(11,59,46,0.18)]">
                        <Shield className="h-3 w-3" /> {ROLE_LABEL[addRole]}
                      </span>
                      {needsDept ? (
                        <>
                          <span className="text-[#7C8A82]">in</span>
                          {addOrgIds.length > 0 ? (
                            addOrgIds.map((oid) => (
                              <span key={oid} className="inline-flex items-center gap-1 rounded-lg bg-[rgba(255,254,251,0.96)] px-2 py-1 text-xs font-bold text-[#0B3B2E] shadow-sm ring-1 ring-[rgba(11,59,46,0.18)]">
                                <Building2 className="h-3 w-3" /> {orgNameById.get(oid) ?? oid}
                              </span>
                            ))
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-lg bg-[rgba(201,169,97,0.16)] px-2 py-1 text-xs font-bold text-[#8A5A08] ring-1 ring-[rgba(201,169,97,0.55)]">
                              <Building2 className="h-3 w-3" /> Pick department(s)
                            </span>
                          )}
                        </>
                      ) : null}
                    </div>
                    {reportsToName && (addRole === 'department_head' || addRole === 'site_engineer') ? (
                      <p className="mt-2 flex flex-wrap items-center gap-1 text-xs text-[#2A1F17]">
                        <ChevronRight className="h-3 w-3 text-[#7C8A82]" />
                        Reports to <span className="font-bold text-[#0B3B2E]">{reportsToName}</span>
                        {reportsToRole ? <span className="text-[#7C8A82]">· {reportsToRole}</span> : null}
                      </p>
                    ) : null}
                    {hintForRole ? (
                      <p className="mt-1.5 text-[11px] text-[#7C8A82]">{hintForRole}</p>
                    ) : null}
                  </div>
                  {drawerAutoResolved ? (
                    <button
                      type="button"
                      onClick={() => setDrawerAdjustOpen((v) => !v)}
                      className="inline-flex items-center gap-1 rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2.5 py-1.5 text-[11px] font-bold text-[#0B3B2E] shadow-sm hover:border-[rgba(51,146,116,0.6)]"
                    >
                      <Sliders className="h-3 w-3" />
                      {drawerAdjustOpen ? 'Hide details' : 'Adjust'}
                      {drawerAdjustOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Primary action — pick the user */}
              <div>
                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#2A1F17]">
                  <UsersIcon className="h-3.5 w-3.5 text-[#1F5A47]" />
                  Select team member
                  <span className="text-[#9F1239]">*</span>
                </label>
                <EpcTeamUserPicker
                  users={drawerCandidates.users} value={addUserId} onChange={setAddUserId}
                  disabled={saving} excludeUserIds={excludeForSlot(addRole, addOrgIds[0])}
                  roleLabelByUserId={drawerCandidates.roleLabelByUserId}
                  placeholder={`Search EPC ${ROLE_LABEL[addRole]}s by name or email…`}
                  emptyHint={`No EPC ${ROLE_LABEL[addRole]}s found. Assign the matching role in EPC from User Management.`}
                />
                <p className="mt-1.5 text-[11px] text-[#7C8A82]">
                  Showing only EPC users with the <span className="font-bold text-[#1F5A47]">{ROLE_LABEL[addRole]}</span> role.
                  {isGovernanceRole(addRole)
                    ? ' Governance is one-seat-per-person, so existing seats are hidden.'
                    : ' No restrictions — the same person can be added to any number of departments.'}
                  {' '}Add new people from{' '}
                  <Link to="/admin/users" className="font-bold text-[#1F5A47] hover:underline">User Management</Link>.
                </p>
              </div>

              {/* Adjust-details (collapsible) */}
              <AnimatePresence initial={false}>
                {showAdjust ? (
                  <motion.div
                    key="adjust-details"
                    initial={drawerAutoResolved ? { opacity: 0, height: 0 } : false}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.18)] bg-[#F8F2E2] p-4"
                  >
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">
                      {drawerAutoResolved ? 'Override auto-detected values' : 'Role & reporting line'}
                    </p>
                    <div>
                      <label className="block text-xs font-bold text-[#2A1F17] mb-1.5">
                        EPC Role
                        {epcRolesQ.isLoading ? (
                          <span className="ml-2 text-[10px] font-normal text-[#7C8A82]">Loading…</span>
                        ) : null}
                      </label>
                      <select
                        className="w-full rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-2.5 text-sm text-[#0B3B2E] outline-none focus:border-[#0B3B2E] focus:ring-[3px] focus:ring-[rgba(166,206,57,0.22)]"
                        value={addRoleId || `tag:${addRole}`}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw.startsWith('tag:')) {
                            // Fallback path — admin picked a bare tier tag
                            // because the dynamic list hasn't loaded yet.
                            setAddRole(raw.slice('tag:'.length) as EpcProjectTeamRole);
                            setAddRoleId('');
                            return;
                          }
                          const picked = epcRoles.find((r) => r.id === raw);
                          if (picked) {
                            setAddRole(picked.tag);
                            setAddRoleId(picked.id);
                          }
                        }}
                      >
                        {epcRoles.length > 0
                          ? epcRoles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name} {r.name.toLowerCase() !== ROLE_LABEL[r.tag].toLowerCase()
                                  ? `· acts as ${ROLE_LABEL[r.tag]}`
                                  : ''}
                              </option>
                            ))
                          : FALLBACK_TIER_TAGS.map((r) => (
                              <option key={`tag:${r}`} value={`tag:${r}`}>{ROLE_LABEL[r]}</option>
                            ))}
                      </select>
                      {epcRolesQ.isError ? (
                        <p className="mt-1 text-[11px] text-[#9F1239]">
                          Couldn&apos;t load EPC roles — using the built-in tier list.
                        </p>
                      ) : null}
                    </div>
                    {needsDept ? (
                      <div>
                        <label className="block text-xs font-bold text-[#2A1F17] mb-1.5">
                          Department
                          <span className="ml-1 text-[10px] font-normal text-[#7C8A82]">
                            {addRole === 'site_engineer' ? '(select one — engineers join one department per project)' : '(select one or more)'}
                          </span>
                        </label>
                        <div className="max-h-44 overflow-y-auto rounded-xl border border-[rgba(11,59,46,0.28)] bg-[rgba(255,254,251,0.98)] divide-y divide-[rgba(11,59,46,0.10)]">
                          {orgUnits.length === 0 ? (
                            <p className="px-3 py-3 text-xs text-[#7C8A82]">No departments yet.</p>
                          ) : orgUnits.map((ou) => {
                            const oid = orgId(ou);
                            const checked = addOrgIds.includes(oid);
                            return (
                              <label
                                key={oid}
                                className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-[rgba(11,59,46,0.05)]"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    setAddOrgIds((prev) => {
                                      if (checked) return prev.filter((x) => x !== oid);
                                      // Site engineers hold one membership per project —
                                      // picking a department replaces the previous choice.
                                      if (addRole === 'site_engineer') return [oid];
                                      return [...prev, oid];
                                    });
                                  }}
                                  className="h-4 w-4 rounded border-[rgba(11,59,46,0.28)] accent-[#1F5A47]"
                                />
                                <span className="text-sm text-[#2A1F17]">{ou.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {addRole === 'site_engineer' ? (
                      <div>
                        <label className="block text-xs font-bold text-[#2A1F17] mb-1.5">
                          Reports to (manager) <span className="text-[#9F1239]">*</span>
                        </label>
                        {addOrgIds.length !== 1 ? (
                          <p className="rounded-lg border border-[rgba(11,59,46,0.18)] bg-[rgba(255,254,251,0.96)] px-3 py-2 text-[11px] text-[#7C8A82]">
                            Select exactly one department above to pick a manager.
                          </p>
                        ) : (managersByOrg.get(addOrgIds[0]!) ?? []).filter((m) => !m.inactive).length === 0 ? (
                          <p className="rounded-lg border border-[rgba(201,169,97,0.55)] bg-[rgba(201,169,97,0.14)] px-3 py-2 text-[11px] text-[#8A5A08]">
                            No managers in this department yet — add a manager first.
                          </p>
                        ) : (
                          <select
                            className="w-full rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-2.5 text-sm text-[#0B3B2E] outline-none focus:border-[#0B3B2E] focus:ring-[3px] focus:ring-[rgba(166,206,57,0.22)]"
                            value={addReportsTo}
                            onChange={(e) => setAddReportsTo(e.target.value)}
                          >
                            <option value="">Select manager…</option>
                            {(managersByOrg.get(addOrgIds[0]!) ?? [])
                              .filter((m) => !m.inactive)
                              .map((m) => (
                                <option key={m.userId} value={m.userId}>
                                  {m.userName ?? m.userId}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>
                    ) : (addRole === 'department_head' || (isGovernanceRole(addRole) && govList.length > 0)) ? (
                      <div>
                        <label className="block text-xs font-bold text-[#2A1F17] mb-1.5">Reports to</label>
                        <select
                          className="w-full rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-2.5 text-sm text-[#0B3B2E] outline-none focus:border-[#0B3B2E] focus:ring-[3px] focus:ring-[rgba(166,206,57,0.22)]"
                          value={addReportsTo}
                          onChange={(e) => setAddReportsTo(e.target.value)}
                        >
                          {addRole === 'department_head' ? (
                            <option value="">Select governance member…</option>
                          ) : (
                            <option value="">Optional (governance chain)</option>
                          )}
                          {govList.map((g) => (
                            <option key={g.userId} value={g.userId}>{g.userName ?? g.userId} · {ROLE_LABEL[g.role]}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </form>
          );
        })()}
      </RightDrawer>

      {/* Conflict / confirm popup — rendered last so it stacks above the drawer */}
      <TeamActionDialog state={teamDialog} onClose={() => setTeamDialog(null)} />

    </div>
  );
}
